import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link, Redirect, useLocation } from "react-router-dom";
import { Typography } from "@mui/material";
import { styled as materialStyled } from "@mui/material/styles";
import { captureException, withScope } from "@sentry/react";

import { Block, ContentHolder } from "commons";

import { Go404Animation } from "./404Animation";
import { LiveChat } from "./LiveChat";
import { Support } from "./Support";
import { routes as allRoutes } from "../helpers/RouteHelpers";

function isClientRoute(path, clientRoutes) {
    let result = false;

    for (const route of clientRoutes) {
        if (path.startsWith(`/${route}`)) {
            result = true;
            break;
        }
    }

    return result;
}

const clientRoutes = Object.keys(allRoutes.client).filter(item => item !== "root");

class PageNotFoundError extends Error {
    constructor(path) {
        super(`404, page not found - ${path}`);
        this.name = "PageNotFoundError";
    }
}

export function FourOhFour({ routes = clientRoutes }) {
    const { pathname } = useLocation();

    useEffect(() => {
        const level = isClientRoute(pathname, routes) ? "info" : "error";

        withScope(scope => {
            scope.setLevel(level);
            captureException(new PageNotFoundError(pathname));
        });
    }, [pathname, routes]);

    if (isClientRoute(pathname, routes)) {
        return (
            <Redirect
                to={{
                    pathname: `/client${pathname}`,
                    state: { is404Redirect: true }
                }}
            />
        );
    }

    return (
        <FlexContentHolder>
            <StyledBlock>
                <Go404Animation />
                <Typography variant="h1" color="primary">
                    Oh, that&apos;s unexpected.
                </Typography>
                <ErrorText>
                    <Paragraph variant="body1">
                        It looks like this page doesn&apos;t exist:
                    </Paragraph>
                    <Paragraph variant="body1">{window.location.href}</Paragraph>
                    <Paragraph variant="body1">
                        Please go{" "}
                        <Link
                            to={{
                                pathname: allRoutes.client.root(),
                                state: { is404Redirect: true }
                            }}
                        >
                            to the main page
                        </Link>{" "}
                        and try again.
                    </Paragraph>
                </ErrorText>
                <Support />
                <LiveChat />
            </StyledBlock>
        </FlexContentHolder>
    );
}

const ErrorText = styled.div`
    margin-top: 1rem;
    word-break: break-word;
`;

const Paragraph = materialStyled(Typography)({
    marginBottom: "1rem"
});

const StyledBlock = styled(Block)`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 400px;
    width: 400px;
    text-align: center;
    color: #000;
    padding-top: 2rem;
    @media (max-width: ${props => props.theme.maxWidth}) {
        width: 100%;
    }
`;

const FlexContentHolder = styled(ContentHolder)`
    display: flex;
    justify-content: center;
    align-items: flex-start;
`;

FourOhFour.propTypes = {
    routes: PropTypes.arrayOf(PropTypes.string)
};
